import { type PropsWithChildren, useCallback, useEffect,useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';
import { BodyText, Switch, VStack } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────

export const NotificationPreferencesControlsContainer = (
  props: PropsWithChildren,
) => {
  return (
    <VStack
      gap={0}
      hasDivider={true}
      dividerColor={theme.colors.OPACITY.BLACK.LIGHTEST}
      style={styles.container}
    >
      {props.children}
    </VStack>
  );
};

export const NotificationPreferencesControlSection = (
  props: PropsWithChildren,
) => {
  return <View style={styles.section}>{props.children}</View>;
};

// ─── Child Components ────────────────────────────────────────────────

export const NotificationsPreferencesControlTitle = (
  props: PropsWithChildren,
) => {
  return (
    <View style={styles.controlTitleContainer}>
      <BodyText sizeMatch={['14']}>{props.children}</BodyText>
    </View>
  );
};

export const NotificationsPreferencesControlToggle = (
  props: NotificationsPreferencesControlToggleProps,
) => {
  const {
    onChange,
    value,
    isLoading,
    category,
    subscriptionType,
  } = props;


  const [isOn, setIsOn] = useState(value);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnChange = useCallback((updatedValue: boolean) => {
    setIsOn(updatedValue);

    onChange({
      category,
      subscriptionType,
      value: updatedValue,
    });
  }, [category, onChange, subscriptionType]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (isLoading) return;

    if (isOn === value) return;
    
    setIsOn(value);
  }, [isLoading, value, isOn]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Switch
      size="medium"
      value={isOn}
      palette="spinach"
      showIcons={false}
      onChange={handleOnChange}
      disabled={isLoading}
    />
  );
};

// ─── Styles ──────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingTop: theme.spacing['4'],
  },
  section: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: theme.spacing['16'],
    alignItems: 'center',
  },
  controlTitleContainer: {
    paddingVertical: theme.spacing['5'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────

type NotificationsPreferencesControlToggleProps = ToggleState & {
  value: boolean;
  isLoading: boolean;
  onChange: (value: ToggleState) => void;
};

type ToggleState = {
  category: string;
  subscriptionType: string;
  value: boolean;
};
